import React, { useEffect, useState } from "react";
import "./Games.css";
import { useSelector, useDispatch } from "react-redux";
import { fetchGames, updateGame, createGame, uploadGameImage } from "../../redux/gamesSlice";
import { fetchIps } from "../../redux/ipSlice";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faCircleCheck, faCircleXmark, faCopy } from '@fortawesome/free-solid-svg-icons';
import Loading from "../../components/Loading/Loading";
import Popup from "../../components/Popup/Popup";
import { safeUrl } from "../../utils/url";

const EMPTY_STORE = { nameStore: "", link: "", imageStore: "", active: true };

const IMAGE_FIELDS = [
    { key: "largeLogo", label: "Logo grande", required: true },
    { key: "smallLogo", label: "Logo pequeño", required: true },
    { key: "background", label: "Fondo", required: true },
    { key: "seasonLogo", label: "Logo de temporada", required: false },
];

const toDateInputValue = (value) => {
    const match = String(value || "").match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : "";
};

const buildInitialForm = (game = {}, mode = "Create") => {
    const baseName = game.name || "";
    const stores = (game.stores || []).map((store) => ({
        nameStore: store?.nameStore || "",
        link: store?.link || "",
        imageStore: store?.imageStore || "",
        active: store?.active ?? true,
    }));

    return {
        name: mode === "Duplicate" && baseName ? `${baseName} (Copy)` : baseName,
        description: game.description || "",
        active: game.active ?? true,
        associatedIP: game.associatedIP?._id || game.associatedIP?.id || (typeof game.associatedIP === "string" ? game.associatedIP : ""),
        gameType: game.gameType || "",
        largeLogo: game.largeLogo || "",
        smallLogo: game.smallLogo || "",
        background: game.background || "",
        seasonLogo: game.seasonLogo || "",
        launchDate: toDateInputValue(game.launchDate),
        stores: stores.length ? stores : [{ ...EMPTY_STORE }],
    };
};

const isStoreEmpty = (store) => !store.nameStore.trim() && !store.link.trim() && !store.imageStore.trim();

// Solo campos de CreateGameDto / UpdateGameDto (la API rechaza cualquier campo extra).
const buildGamePayload = (form) => {
    const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        active: Boolean(form.active),
        associatedIP: form.associatedIP,
        gameType: form.gameType.trim(),
        largeLogo: form.largeLogo.trim(),
        smallLogo: form.smallLogo.trim(),
        background: form.background.trim(),
        stores: form.stores
            .filter((store) => !isStoreEmpty(store))
            .map((store) => ({
                nameStore: store.nameStore.trim(),
                link: store.link.trim(),
                imageStore: store.imageStore.trim(),
                active: Boolean(store.active),
            })),
    };

    if (form.seasonLogo.trim()) payload.seasonLogo = form.seasonLogo.trim();
    if (form.launchDate) payload.launchDate = form.launchDate;

    return payload;
};

const isFormComplete = (form) =>
    Boolean(
        form.name.trim() &&
        form.description.trim() &&
        form.associatedIP &&
        form.gameType.trim() &&
        form.largeLogo.trim() &&
        form.smallLogo.trim() &&
        form.background.trim() &&
        form.stores.every((store) =>
            isStoreEmpty(store) || (store.nameStore.trim() && store.link.trim() && store.imageStore.trim())
        )
    );

// Campo de URL de imagen con botón para subirla a POST /media/images.
function ImageUrlInput({ value, onChange, onUpload, uploading, placeholder = "https://..." }) {
    return (
        <div className="games-popup-image-input">
            <input
                type="url"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                placeholder={placeholder}
            />
            <label className={`games-popup-upload${uploading ? " is-uploading" : ""}`}>
                {uploading ? "Subiendo..." : "Subir"}
                <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    disabled={uploading}
                    onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = "";
                        if (file) onUpload(file);
                    }}
                />
            </label>
        </div>
    );
}

export default function Games() {
    const dispatch = useDispatch();
    const { games, statusGames, error: gamesError, statusSave } = useSelector(state => state.games);
    const isSaving = statusSave === "loading";
    const { ips, statusIps } = useSelector(state => state.ips);
    const [showPopup, setShowPopup] = useState(false);
    const [gameUpdate, setGameUpdate] = useState({});
    const [typeOption, setTypeOption] = useState('');
    const [form, setForm] = useState(buildInitialForm());
    const [uploadingField, setUploadingField] = useState("");
    const [formError, setFormError] = useState("");

    useEffect(() => {
        if (statusGames === 'idle') {
            dispatch(fetchGames());
        }
    }, [statusGames, dispatch]);

    useEffect(() => {
        if (statusIps === 'idle') {
            dispatch(fetchIps());
        }
    }, [statusIps, dispatch]);

    const openPopup = (mode, game = {}) => {
        setTypeOption(mode);
        setGameUpdate(game);
        setForm(buildInitialForm(game, mode));
        setFormError("");
        setShowPopup(true);
    };

    const closePopup = () => {
        setShowPopup(false);
        setTypeOption('');
        setGameUpdate({});
        setForm(buildInitialForm());
        setFormError("");
    };

    const handleFormChange = (event) => {
        const { name, type, value, checked } = event.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleStoreChange = (index, key, value) => {
        setForm((prev) => {
            const nextStores = [...prev.stores];
            nextStores[index] = {
                ...nextStores[index],
                [key]: value,
            };

            return {
                ...prev,
                stores: nextStores,
            };
        });
    };

    const addStore = () => {
        setForm((prev) => ({
            ...prev,
            stores: [...prev.stores, { ...EMPTY_STORE }],
        }));
    };

    // Sube la imagen y guarda la URL pública en el campo indicado.
    const handleUpload = async (fieldId, file, applyUrl) => {
        setUploadingField(fieldId);
        setFormError("");
        const result = await dispatch(uploadGameImage({ file, folder: "games" }));
        setUploadingField("");
        if (uploadGameImage.fulfilled.match(result) && result.payload?.url) {
            applyUrl(result.payload.url);
        } else {
            setFormError(result.payload || "No se pudo subir la imagen");
        }
    };

    const removeStore = (index) => {
        setForm((prev) => {
            if (prev.stores.length === 1) {
                return {
                    ...prev,
                    stores: [{ ...EMPTY_STORE }],
                };
            }

            return {
                ...prev,
                stores: prev.stores.filter((_, currentIndex) => currentIndex !== index),
            };
        });
    };

    const handleConfirm = async () => {
        if (!isFormComplete(form) || isSaving) {
            return;
        }

        const payload = buildGamePayload(form);
        const result = typeOption === "Create" || typeOption === "Duplicate"
            ? await dispatch(createGame(payload))
            : await dispatch(updateGame({ id: gameUpdate._id, updates: payload }));

        if (createGame.fulfilled.match(result) || updateGame.fulfilled.match(result)) {
            closePopup();
        } else {
            setFormError(result.payload || "No se pudo guardar el juego");
        }
    };

    return (
        statusGames === "loading" ? (<Loading />) : (
            <article className="container-games">
                <div className="container-games-filter">
                    <h1 className="title">
                        GAMES
                        <hr />
                    </h1>
                    <button onClick={() => openPopup("Create")}>Create New</button>
                </div>
                <p> There are <span style={{ color: "#00E0FF", fontWeight: "Bold" }}>{games.length}</span> games created.</p>
                {statusGames === "failed" && !showPopup && gamesError && <p className="games-error">{gamesError}</p>}
                <section className="games-table-wrap">
                    <table className="games-table">
                        <thead>
                            <tr>
                                <th>Id</th>
                                <th>Name</th>
                                <th>Description</th>
                                <th>Active</th>
                                <th>Associated IP</th>
                                <th>Game Type</th>
                                <th>Store</th>
                                <th>Options</th>
                            </tr>
                        </thead>
                        <tbody>
                            {games.length === 0 ? (
                                <tr>
                                    <td className="games-empty" colSpan={8}>No games found.</td>
                                </tr>
                            ) : (
                                games.map((game, index) => (
                                    <tr key={game._id}>
                                        <td>{index + 1}</td>
                                        <td className="games-name-cell">{game.name}</td>
                                        <td>{game.description}</td>
                                        <td>
                                            {game.active ? (
                                                <FontAwesomeIcon className="icon-active" icon={faCircleCheck} size="lg" />
                                            ) : (
                                                <FontAwesomeIcon className="icon-inactive" icon={faCircleXmark} size="lg" />
                                            )}
                                        </td>
                                        <td>{game.associatedIP?.name || "-"}</td>
                                        <td>{game.gameType || "-"}</td>
                                        <td className="txt-stores">
                                            {(game.stores || []).map((store, index2) => (
                                                <span key={`${game._id}-store-${index2}`}>
                                                    <a href={safeUrl(store.link)} target="_blank" rel="noreferrer">
                                                        {store.nameStore}
                                                    </a>
                                                    {index2 < (game.stores || []).length - 1 && " - "}
                                                </span>
                                            ))}
                                            {(!game.stores || game.stores.length === 0) && "-"}
                                        </td>
                                        <td className="games-options-cell">
                                            <FontAwesomeIcon className="icon" onClick={() => openPopup("Update", game)} icon={faPenToSquare} size="lg" />
                                            <FontAwesomeIcon className="icon" onClick={() => openPopup("Duplicate", game)} icon={faCopy} size="lg" />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </section>
                <Popup
                    isOpen={showPopup}
                    title={typeOption === "Update" ? "Editar Juego" : "Crear Juego"}
                    onClose={closePopup}
                    onCancel={closePopup}
                    onConfirm={handleConfirm}
                    confirmText={isSaving ? "Guardando..." : typeOption === "Update" ? "Guardar cambios" : "Crear juego"}
                    confirmDisabled={!isFormComplete(form) || Boolean(uploadingField) || isSaving}
                    cancelDisabled={isSaving}
                    closeOnOverlay={!isSaving}
                    maxWidth="760px"
                    height="60vh"
                >
                    <div className="games-popup-form">
                        <div className="games-popup-grid">
                            <label className="games-popup-field games-popup-field--checkbox">
                                <span>Activo</span>
                                <input
                                    type="checkbox"
                                    name="active"
                                    checked={form.active}
                                    onChange={handleFormChange}
                                />
                            </label>

                            <label className="games-popup-field games-popup-field--full">
                                <span>Nombre</span>
                                <input
                                    type="text"
                                    name="name"
                                    value={form.name}
                                    onChange={handleFormChange}
                                    required
                                />
                            </label>

                            <label className="games-popup-field games-popup-field--full">
                                <span>Descripción</span>
                                <textarea
                                    name="description"
                                    value={form.description}
                                    onChange={handleFormChange}
                                    rows={4}
                                    required
                                />
                            </label>

                            <label className="games-popup-field">
                                <span>IP asociada</span>
                                <select
                                    name="associatedIP"
                                    value={form.associatedIP}
                                    onChange={handleFormChange}
                                    required
                                >
                                    <option value="">Seleccionar IP</option>
                                    {ips.map((ip) => (
                                        <option value={ip._id || ip.id || ip.name} key={ip._id || ip.id || ip.name}>
                                            {ip.name}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label className="games-popup-field">
                                <span>Game Type</span>
                                <input
                                    type="text"
                                    name="gameType"
                                    value={form.gameType}
                                    onChange={handleFormChange}
                                    placeholder="Ej: Web, Mobile"
                                    required
                                />
                            </label>

                            <label className="games-popup-field">
                                <span>Fecha de lanzamiento</span>
                                <input
                                    type="date"
                                    name="launchDate"
                                    value={form.launchDate}
                                    onChange={handleFormChange}
                                />
                            </label>

                            {IMAGE_FIELDS.map((field) => (
                                <div className="games-popup-field" key={field.key}>
                                    <span>{field.label}{field.required ? "" : " (opcional)"}</span>
                                    <ImageUrlInput
                                        value={form[field.key]}
                                        onChange={(value) => setForm((prev) => ({ ...prev, [field.key]: value }))}
                                        uploading={uploadingField === field.key}
                                        onUpload={(file) =>
                                            handleUpload(field.key, file, (url) =>
                                                setForm((prev) => ({ ...prev, [field.key]: url }))
                                            )
                                        }
                                    />
                                </div>
                            ))}

                            <div className="games-popup-field games-popup-field--full">
                                <span>Stores</span>
                                <div className="games-popup-stores">
                                    {form.stores.map((store, index) => (
                                        <div className="games-popup-store-row" key={`store-${index}`}>
                                            <input
                                                type="text"
                                                value={store.nameStore}
                                                onChange={(event) => handleStoreChange(index, "nameStore", event.target.value)}
                                                placeholder="Nombre de la store"
                                            />
                                            <input
                                                type="url"
                                                value={store.link}
                                                onChange={(event) => handleStoreChange(index, "link", event.target.value)}
                                                placeholder="https://..."
                                            />
                                            <ImageUrlInput
                                                value={store.imageStore}
                                                onChange={(value) => handleStoreChange(index, "imageStore", value)}
                                                placeholder="Imagen de la store"
                                                uploading={uploadingField === `store-${index}`}
                                                onUpload={(file) =>
                                                    handleUpload(`store-${index}`, file, (url) =>
                                                        handleStoreChange(index, "imageStore", url)
                                                    )
                                                }
                                            />
                                            <label className="games-popup-store-active" title="Store visible">
                                                <input
                                                    type="checkbox"
                                                    checked={store.active}
                                                    onChange={(event) => handleStoreChange(index, "active", event.target.checked)}
                                                />
                                                Activa
                                            </label>
                                            <button
                                                type="button"
                                                className="games-popup-store-remove"
                                                onClick={() => removeStore(index)}
                                                aria-label="Eliminar store"
                                            >
                                                X
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    className="games-popup-add-store"
                                    onClick={addStore}
                                >
                                    + Agregar store
                                </button>
                            </div>
                        </div>
                        {formError && <p className="games-error">{formError}</p>}
                    </div>
                </Popup>
            </article>
        )
    );
}